import React, { useState, useEffect } from "react";
import {
  Grid,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Divider,
  Checkbox,
  FormControlLabel,
  Box,
  Switch,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Tooltip from "@mui/material/Tooltip";
import {
  CREATE,
  UPDATE,
  REQUEST_TYPES as requestTypes,
} from "../../../utils/constants";
import InlineDropdown from "../../common/InlineDropdown/InlineDropdown";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

const mapRequestType = (requestType) => ({
  id: requestType,
  name: requestType,
});
const mappedRequestTypes = [...(requestTypes || []).map(mapRequestType)];

const UserModelDialog = ({
  openModal,
  setOpenModal,
  users,
  sfcRegions = [],
  setSelectedUserEmail,
  selectedUserDetail = {},
  setSelectedUserDetail = () => {},
  setUserFormData = () => {},
  mode = CREATE, // "create" or "update"
} = {}) => {
  const [globalReadWriteAccess, setGlobalReadWriteAccess] = useState(false);
  const [globalReadOnlyAccess, setGlobalReadOnlyAccess] = useState(false);
  const [sfcRegionsWithoutGlobal, setSfcRegionsWithoutGlobal] = useState([]);
  const [globalRegion, setGlobalRegion] = useState([]);

  useEffect(() => {
    const filteredSfcRegionsWithoutGlobal = sfcRegions.filter(
      (region) => region.regionName?.toLowerCase() !== "global",
    );
    setSfcRegionsWithoutGlobal(filteredSfcRegionsWithoutGlobal);

    const filteredGlobalRegion = sfcRegions.find(
      (region) => region.regionName?.toLowerCase() === "global",
    );
    setGlobalRegion(filteredGlobalRegion);
  }, [sfcRegions]);

  const [formData, setFormData] = useState({
    email: null,
    firstName: null,
    lastName: null,
    lanId: null,
    requestType: null,
    active: true,
    applicationAdministrator: false,
    admin: false,
    globalTechSupport: false,
    userAccessProvisioner: false,
  });

  const [touched, setTouched] = useState({
    email: false,
    firstName: false,
    lastName: false,
    lanId: false,
    requestType: false,
    active: false,
    applicationAdministrator: false,
    admin: false,
    globalTechSupport: false,
    userAccessProvisioner: false,
  });

  const [sfcAccess, setSfcAccess] = useState(() => {
    const access = {};
    sfcRegionsWithoutGlobal?.forEach((region) => {
      region?.sfcs?.forEach((sfc) => {
        access[`${region.regionId}-${sfc.sfcId}`] = {
          read: false,
          write: false,
        };
      });
    });
    return access;
  });

  useEffect(() => {
    if (selectedUserDetail && mode === UPDATE) {
      setFormData({
        email: selectedUserDetail.email || null,
        firstName: selectedUserDetail.firstName || null,
        lastName: selectedUserDetail.lastName || null,
        lanId: selectedUserDetail.lanId || null,
        requestType: selectedUserDetail.requestType || null,
        active: selectedUserDetail.active,
        applicationAdministrator: selectedUserDetail.applicationAdministrator,
        admin: selectedUserDetail.admin,
        globalTechSupport: selectedUserDetail.globalTechSupport,
        userAccessProvisioner: selectedUserDetail.userAccessProvisioner,
      });

      const updatedAccess = {};

      sfcRegionsWithoutGlobal.forEach((region) => {
        region?.sfcs?.forEach((sfc) => {
          const userRegion = selectedUserDetail?.regions?.find(
            (r) => r.regionId === region.regionId,
          );
          const userSfc = userRegion?.sfcs?.find((u) => u.sfcId === sfc.sfcId);

          const read = !!userSfc;
          const write = !!userSfc?.writable;

          updatedAccess[`${region.regionId}-${sfc.sfcId}`] = {
            read,
            write,
          };
        });
      });

      setSfcAccess(updatedAccess);

      const userGlobalRegion = selectedUserDetail?.regions?.find(
        (r) => r.regionId === globalRegion?.regionId,
      );

      const globalSfcList = globalRegion?.sfcs || [];
      const hasGlobalPermissions =
        !!userGlobalRegion &&
        globalSfcList.length > 0 &&
        globalSfcList.every((sfc) =>
          userGlobalRegion?.sfcs?.some((u) => u.sfcId === sfc.sfcId),
        );

      const hasGlobalReadOnly =
        hasGlobalPermissions &&
        globalSfcList.every((sfc) => {
          const userSfc = userGlobalRegion?.sfcs?.find(
            (u) => u.sfcId === sfc.sfcId,
          );
          return userSfc && !userSfc.writable;
        });

      const hasGlobalReadWrite =
        hasGlobalPermissions &&
        globalSfcList.every((sfc) => {
          const userSfc = userGlobalRegion?.sfcs?.find(
            (u) => u.sfcId === sfc.sfcId,
          );
          return userSfc && userSfc.writable;
        });

      setGlobalReadOnlyAccess(hasGlobalReadOnly);
      setGlobalReadWriteAccess(hasGlobalReadWrite);
    }
  }, [selectedUserDetail, mode, sfcRegionsWithoutGlobal, globalRegion]);

  const handleAccessChange = (key, type) => (e) => {
    const checked = e.target.checked;

    setGlobalReadWriteAccess(false);
    setGlobalReadOnlyAccess(false);

    setSfcAccess((prev) => {
      const current = prev[key] || { read: false, write: false };

      if (type === "write" && checked) {
        return {
          ...prev,
          [key]: {
            read: true,
            write: true,
          },
        };
      }

      return {
        ...prev,
        [key]: {
          ...current,
          [type]: checked,
        },
      };
    });
  };

  const isEmailDuplicate = (email) => {
    if (!users || !Array.isArray(users)) return false;
    return users.some(
      (user) => user.email?.toLowerCase() === email?.toLowerCase(),
    );
  };

  const handleBlur = (field) => {
    setTouched((prevTouched) => ({ ...prevTouched, [field]: true }));

    setFormData((prevData) => ({
      ...prevData,
      [field]: prevData[field]?.trim(),
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const newValue = value?.startsWith(" ") ? value?.trimStart() : value;
    setFormData((prevData) => ({ ...prevData, [name]: newValue }));
  };

  const resetForm = () => {
    setFormData({
      email: null,
      firstName: null,
      lastName: null,
      lanId: null,
      requestType: null,
      active: true,
      applicationAdministrator: false,
      admin: false,
      globalTechSupport: false,
      userAccessProvisioner: false,
    });
    setTouched({
      email: false,
      firstName: false,
      lastName: false,
      lanId: false,
      requestType: false,
      active: false,
      applicationAdministrator: false,
      admin: false,
      globalTechSupport: false,
      userAccessProvisioner: false,
    });
    const resetAccess = {};
    sfcRegionsWithoutGlobal?.forEach((region) => {
      region?.sfcs?.forEach((sfc) => {
        resetAccess[`${region.regionId}-${sfc.sfcId}`] = {
          read: false,
          write: false,
        };
      });
    });
    setSfcAccess(resetAccess);
    setGlobalReadWriteAccess(false);
    setGlobalReadOnlyAccess(false);
  };

  const handleGlobalReadWriteAccessChange = (e) => {
    const checked = e.target.checked;
    setGlobalReadWriteAccess(checked);

    if (checked) {
      setGlobalReadOnlyAccess(false);
    }

    setSfcAccess((prev) => {
      const updatedAccess = { ...prev };

      sfcRegionsWithoutGlobal?.forEach((region) => {
        region?.sfcs?.forEach((sfc) => {
          const key = `${region.regionId}-${sfc.sfcId}`;
          updatedAccess[key] = {
            read: checked,
            write: checked,
          };
        });
      });

      return updatedAccess;
    });
  };

  const handleGlobalReadOnlyAccessChange = (e) => {
    const checked = e.target.checked;
    setGlobalReadOnlyAccess(checked);

    if (checked) {
      setGlobalReadWriteAccess(false);
    }

    setSfcAccess((prev) => {
      const updatedAccess = { ...prev };

      sfcRegionsWithoutGlobal?.forEach((region) => {
        region?.sfcs?.forEach((sfc) => {
          const key = `${region.regionId}-${sfc.sfcId}`;
          updatedAccess[key] = {
            read: checked,
            write: false,
          };
        });
      });

      return updatedAccess;
    });
  };

  const handleCloseModal = (event, reason) => {
    if (reason && reason === "backdropClick") {
      return; // Prevent closing when clicked outside
    }
    if (mode === UPDATE) {
      setSelectedUserEmail(null);
      setSelectedUserDetail({});
    }
    resetForm();
    setOpenModal(false);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();

    const selectedAccess = [];

    if (globalReadWriteAccess) {
      globalRegion?.sfcs?.forEach((sfc) => {
        selectedAccess.push({
          regionId: globalRegion.regionId,
          regionName: globalRegion.regionName,
          sfcId: sfc.sfcId,
          businessEntityID: sfc.businessEntityID,
          read: true,
          write: true,
        });
      });
    } else if (globalReadOnlyAccess) {
      globalRegion?.sfcs?.forEach((sfc) => {
        selectedAccess.push({
          regionId: globalRegion.regionId,
          regionName: globalRegion.regionName,
          sfcId: sfc.sfcId,
          businessEntityID: sfc.businessEntityID,
          read: true,
          write: false,
        });
      });
    } else {
      // Otherwise, add individual SFCs based on sfcAccess state
      sfcRegionsWithoutGlobal?.forEach((region) => {
        region?.sfcs?.forEach((sfc) => {
          const key = `${region.regionId}-${sfc.sfcId}`;
          const access = sfcAccess[key] || { read: false, write: false };

          if (access.read || access.write) {
            selectedAccess.push({
              regionId: region.regionId,
              regionName: region.regionName,
              sfcId: sfc.sfcId,
              businessEntityID: sfc.businessEntityID,
              read: !!access.read,
              write: !!access.write,
            });
          }
        });
      });
    }

    const finalFormData = {
      ...formData,
      sfcPermissions: selectedAccess,
      mode: mode,
    };

    setUserFormData(finalFormData || {});
    // if (mode === UPDATE) {
    setSelectedUserEmail(null);
    // }
    setOpenModal(false);
    resetForm();
  };

  const [expandedRegions, setExpandedRegions] = useState({});

  const toggleRegionExpansion = (regionId) => {
    setExpandedRegions((prev) => ({
      ...prev,
      [regionId]: !prev[regionId],
    }));
  };

  const handleParentAccessChange = (regionId, type) => (e) => {
    const checked = e.target.checked;

    setGlobalReadWriteAccess(false);
    setGlobalReadOnlyAccess(false);

    setSfcAccess((prevAccess) => {
      const updatedAccess = { ...prevAccess };
      const region = sfcRegionsWithoutGlobal?.find(
        (r) => r.regionId === regionId,
      );

      region?.sfcs?.forEach((sfc) => {
        const key = `${regionId}-${sfc.sfcId}`;
        updatedAccess[key] = {
          ...updatedAccess[key],
          [type]: checked,
          ...(type === "write" && checked ? { read: true } : {}),
        };
      });

      return updatedAccess;
    });
  };

  const areAllChildrenChecked = (regionId, type) => {
    const region = sfcRegionsWithoutGlobal?.find(
      (r) => r.regionId === regionId,
    );
    return region?.sfcs?.every(
      (sfc) => sfcAccess[`${regionId}-${sfc.sfcId}`]?.[type],
    );
  };

  const isAnyChildChecked = (regionId, type) => {
    const region = sfcRegionsWithoutGlobal?.find(
      (r) => r.regionId === regionId,
    );
    return region?.sfcs?.some(
      (sfc) => sfcAccess[`${regionId}-${sfc.sfcId}`]?.[type],
    );
  };

  return (
    <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
      <DialogTitle>
        {mode === UPDATE
          ? `Update ${formData?.firstName} ${formData?.lastName} Information`
          : "Create User"}
        <IconButton
          aria-label="close"
          onClick={handleCloseModal}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
          size="large"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider sx={{ mt: 0.5 }} />
      <DialogContent>
        <form onSubmit={handleFormSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField
                label="Email"
                style={{ width: 260 }}
                required
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                onBlur={() => handleBlur("email")}
                margin="normal"
                autoComplete="off"
                disabled={mode === UPDATE}
                error={
                  (touched.email &&
                    !formData.email?.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) ||
                  (mode !== UPDATE && isEmailDuplicate(formData.email))
                }
                helperText={
                  touched.email &&
                  !formData.email?.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)
                    ? "Please enter a valid email."
                    : mode !== UPDATE && isEmailDuplicate(formData.email)
                      ? "Email already exists!"
                      : ""
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="First Name"
                style={{ width: 260 }}
                required
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                onBlur={() => handleBlur("firstName")}
                margin="normal"
                autoComplete="off"
                error={touched.firstName && !formData.firstName}
                helperText={
                  touched.firstName &&
                  !formData.firstName &&
                  "First name is required."
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Last Name"
                style={{ width: 260 }}
                required
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                onBlur={() => handleBlur("lastName")}
                margin="normal"
                autoComplete="off"
                error={touched.lastName && !formData.lastName}
                helperText={
                  touched.lastName &&
                  !formData.lastName &&
                  "Last name is required."
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Lan Id (Optional)"
                style={{ width: 260 }}
                name="lanId"
                value={formData.lanId}
                onChange={handleInputChange}
                onBlur={() => handleBlur("lanId")}
                margin="normal"
                autoComplete="off"
              />
            </Grid>

            <Grid
              item
              xs={4}
              sx={{
                marginTop: -1,
              }}
            >
              <InlineDropdown
                label="Request Type"
                labelId="requestType"
                options={mappedRequestTypes}
                selectedValue={formData.requestType}
                placeholder={"Select Request Type"}
                dropdownWidth={260}
                size={"medium"}
                handleChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    requestType: value,
                  }))
                }
                disabled={mode === UPDATE}
              />
            </Grid>
            <Grid item xs={4}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  width: 280,
                }}
              >
                <Typography
                  sx={{
                    mr: 2,
                    fontWeight: 500,
                    color: "#838383",
                  }}
                >
                  Active Status
                  <Tooltip
                    title="Active status enables / disables the user login access"
                    placement="top"
                  >
                    <InfoOutlinedIcon
                      sx={{
                        ml: 0.5,
                        mb: 1,
                        fontSize: 18,
                        cursor: "pointer",
                        color: "#4992f7",
                      }}
                    />
                  </Tooltip>
                  :
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.active || false}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          active: e.target.checked,
                        }))
                      }
                      color="success"
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#fff",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: "#4caf50",
                            opacity: 1,
                          },
                        "& .MuiSwitch-switchBase": {
                          "&:not(.Mui-checked) + .MuiSwitch-track": {
                            backgroundColor: "#f44336",
                            opacity: 1,
                          },
                        },
                        "& .MuiSwitch-track": {
                          backgroundColor: "#bdbdbd",
                          opacity: 1,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ ml: 1, fontWeight: 500 }}>
                      {formData.active ? "(Active)" : "(Inactive)"}
                    </Typography>
                  }
                  sx={{ mt: 1 }}
                />
              </Box>
            </Grid>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                width: "100%",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography
                  sx={{
                    mr: 1,
                    fontWeight: 500,
                    marginTop: 1,
                    color: "#838383",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  Provide Application Administrator Access
                  <Tooltip
                    title="Granting Application Administrator access allows the user to manage all reports (except Admin Management)"
                    placement="top"
                  >
                    <InfoOutlinedIcon
                      sx={{
                        ml: 0.5,
                        mb: 1,
                        fontSize: 18,
                        cursor: "pointer",
                        color: "#4992f7",
                      }}
                    />
                  </Tooltip>
                  :
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.applicationAdministrator || false}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          applicationAdministrator: e.target.checked,
                        }))
                      }
                      color="success"
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#fff",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: "#4caf50",
                            opacity: 1,
                          },
                        "& .MuiSwitch-switchBase": {
                          "&:not(.Mui-checked) + .MuiSwitch-track": {
                            backgroundColor: "#f44336",
                            opacity: 1,
                          },
                        },
                        "& .MuiSwitch-track": {
                          backgroundColor: "#bdbdbd",
                          opacity: 1,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ ml: 1, fontWeight: 500 }}>
                      {formData.applicationAdministrator ? "(Yes)" : "(No)"}
                    </Typography>
                  }
                  sx={{ mt: 1 }}
                />
              </Box>

              <Typography
                variant="caption"
                sx={{ color: "#666", mt: 0.5, ml: 0.5 }}
              >
                (Note: Application Administrator access grants full control over
                the reports except Admin Management)
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                width: "100%",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography
                  sx={{
                    mr: 1,
                    fontWeight: 500,
                    marginTop: 1,
                    color: "#838383",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  Provide Full Admin Access
                  <Tooltip
                    title="Granting full Admin access allows full control over the application (except managing the users). Use with caution."
                    placement="top"
                  >
                    <InfoOutlinedIcon
                      sx={{
                        ml: 0.5,
                        mb: 1,
                        fontSize: 18,
                        cursor: "pointer",
                        color: "#4992f7",
                      }}
                    />
                  </Tooltip>
                  :
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.admin || false}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          admin: e.target.checked,
                        }))
                      }
                      color="success"
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#fff",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: "#4caf50",
                            opacity: 1,
                          },
                        "& .MuiSwitch-switchBase": {
                          "&:not(.Mui-checked) + .MuiSwitch-track": {
                            backgroundColor: "#f44336",
                            opacity: 1,
                          },
                        },
                        "& .MuiSwitch-track": {
                          backgroundColor: "#bdbdbd",
                          opacity: 1,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ ml: 1, fontWeight: 500 }}>
                      {formData.admin ? "(Yes)" : "(No)"}
                    </Typography>
                  }
                  sx={{ mt: 1 }}
                />
              </Box>

              <Typography
                variant="caption"
                sx={{ color: "#666", mt: 0.5, ml: 0.5 }}
              >
                (Note: Full Admin access grants full control over the
                application except managing the users)
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                width: "100%",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography
                  sx={{
                    mr: 1,
                    fontWeight: 500,
                    marginTop: 1,
                    color: "#838383",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  Provide Global Tech Support Access
                  <Tooltip
                    title="Granting Global Tech Support access allows to the dashboard and profile page for the application monitoring purpose"
                    placement="top"
                  >
                    <InfoOutlinedIcon
                      sx={{
                        ml: 0.5,
                        mb: 1,
                        fontSize: 18,
                        cursor: "pointer",
                        color: "#4992f7",
                      }}
                    />
                  </Tooltip>
                  :
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.globalTechSupport || false}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          globalTechSupport: e.target.checked,
                        }))
                      }
                      color="success"
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#fff",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: "#4caf50",
                            opacity: 1,
                          },
                        "& .MuiSwitch-switchBase": {
                          "&:not(.Mui-checked) + .MuiSwitch-track": {
                            backgroundColor: "#f44336",
                            opacity: 1,
                          },
                        },
                        "& .MuiSwitch-track": {
                          backgroundColor: "#bdbdbd",
                          opacity: 1,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ ml: 1, fontWeight: 500 }}>
                      {formData.globalTechSupport ? "(Yes)" : "(No)"}
                    </Typography>
                  }
                  sx={{ mt: 1 }}
                />
              </Box>

              <Typography
                variant="caption"
                sx={{ color: "#666", mt: 0.5, ml: 0.5 }}
              >
                (Note: Global Tech Support access allows to view the dashboard
                and profile for application monitoring purpose)
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                width: "100%",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography
                  sx={{
                    mr: 1,
                    fontWeight: 500,
                    marginTop: 1,
                    color: "#838383",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  Provide User Provisioner Access
                  <Tooltip
                    title="Granting User Provisioner access allows to manage the users"
                    placement="top"
                  >
                    <InfoOutlinedIcon
                      sx={{
                        ml: 0.5,
                        mb: 1,
                        fontSize: 18,
                        cursor: "pointer",
                        color: "#4992f7",
                      }}
                    />
                  </Tooltip>
                  :
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.userAccessProvisioner || false}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          userAccessProvisioner: e.target.checked,
                        }))
                      }
                      color="success"
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#fff",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: "#4caf50",
                            opacity: 1,
                          },
                        "& .MuiSwitch-switchBase": {
                          "&:not(.Mui-checked) + .MuiSwitch-track": {
                            backgroundColor: "#f44336",
                            opacity: 1,
                          },
                        },
                        "& .MuiSwitch-track": {
                          backgroundColor: "#bdbdbd",
                          opacity: 1,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ ml: 1, fontWeight: 500 }}>
                      {formData.userAccessProvisioner ? "(Yes)" : "(No)"}
                    </Typography>
                  }
                  sx={{ mt: 1 }}
                />
              </Box>

              <Typography
                variant="caption"
                sx={{ color: "#666", mt: 0.5, ml: 0.5 }}
              >
                (Note: User Provisioner access allows to manage the users)
              </Typography>
            </Box>
          </Grid>

          {/* New Global Access Checkbox with improved styling and text */}
          {sfcRegionsWithoutGlobal.length > 0 && (
            <>
              <Box
                sx={{
                  mt: 3,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Typography sx={{ minWidth: 120 }}>
                  Global Access
                  <Tooltip
                    title="Granting Global access allow user to access all the SFC Read / SFC Write"
                    placement="top"
                  >
                    <InfoOutlinedIcon
                      sx={{
                        ml: 0.5,
                        mb: 1,
                        fontSize: 18,
                        cursor: "pointer",
                        color: "#4992f7",
                      }}
                    />
                  </Tooltip>
                  :
                </Typography>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={globalReadOnlyAccess}
                      onChange={handleGlobalReadOnlyAccessChange}
                      name="globalReadOnlyAccess"
                      color="primary"
                      disabled={
                        formData?.admin || formData?.applicationAdministrator
                      }
                    />
                  }
                  label="Read"
                  sx={{ mr: 2 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={globalReadWriteAccess}
                      onChange={handleGlobalReadWriteAccessChange}
                      name="globalReadWriteAccess"
                      color="primary"
                      disabled={
                        formData?.admin || formData?.applicationAdministrator
                      }
                    />
                  }
                  label="Write"
                />
              </Box>
              <Typography variant="caption" sx={{ color: "#666", ml: 0.5 }}>
                (Note: Global access grants control to all SFC's and does not
                include Admin Management)
              </Typography>
            </>
          )}

          {/* Region & SFC Table with Checkboxes */}
          <Typography gutterBottom sx={{ mt: 4 }}>
            Please select Regions & Business Entity ID
          </Typography>
          <Typography
            variant="caption"
            display="block"
            sx={{ mt: 1, mb: 2, color: "text.secondary" }}
          >
            <strong>Note: </strong>You can select all SFCs at the region level
            using the Read/Write checkboxes, or expand the region to choose
            individual SFC permissions.
          </Typography>
          <Divider sx={{ mt: 0.5 }} />
          <Table
            size="small"
            aria-label="region-sfc-table"
            sx={{
              background: "#f8f8f8",
              border: "1px solid #dfdfdf",
            }}
          >
            <TableHead
              sx={{
                background: "#d5d5d5",
              }}
            >
              <TableRow>
                <TableCell>
                  <strong>Region</strong>
                </TableCell>
                <TableCell>
                  <strong>SFC</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>Read</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>Write</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sfcRegionsWithoutGlobal?.map((region) => {
                const isExpanded = expandedRegions[region.regionId] || false;

                return (
                  <React.Fragment key={region.regionId}>
                    {/* Parent Row */}
                    <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                      <TableCell>
                        <IconButton
                          onClick={() => toggleRegionExpansion(region.regionId)}
                        >
                          {isExpanded ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )}
                        </IconButton>
                        <strong>{region.regionName}</strong>
                      </TableCell>
                      <TableCell
                        colSpan={1}
                      >{`All SFC's of ${region.regionName} Region`}</TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={areAllChildrenChecked(
                            region.regionId,
                            "read",
                          )}
                          indeterminate={
                            !areAllChildrenChecked(region.regionId, "read") &&
                            isAnyChildChecked(region.regionId, "read")
                          }
                          onChange={handleParentAccessChange(
                            region.regionId,
                            "read",
                          )}
                          disabled={
                            globalReadWriteAccess ||
                            globalReadOnlyAccess ||
                            areAllChildrenChecked(region.regionId, "write") ||
                            formData?.admin ||
                            formData?.applicationAdministrator
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={areAllChildrenChecked(
                            region.regionId,
                            "write",
                          )}
                          indeterminate={
                            !areAllChildrenChecked(region.regionId, "write") &&
                            isAnyChildChecked(region.regionId, "write")
                          }
                          onChange={handleParentAccessChange(
                            region.regionId,
                            "write",
                          )}
                          disabled={
                            globalReadWriteAccess ||
                            globalReadOnlyAccess ||
                            formData?.admin ||
                            formData?.applicationAdministrator
                          }
                        />
                      </TableCell>
                    </TableRow>

                    {/* Child Rows (Only visible if expanded) */}
                    {isExpanded &&
                      region?.sfcs?.map((sfc) => {
                        const key = `${region.regionId}-${sfc.sfcId}`;
                        return (
                          <TableRow key={key}>
                            <TableCell />
                            <TableCell>
                              {sfc.businessEntityID} (Currency:{" "}
                              {sfc.localCurrency} - {sfc.country})
                            </TableCell>
                            <TableCell align="center">
                              <Checkbox
                                checked={sfcAccess[key]?.read || false}
                                onChange={handleAccessChange(key, "read")}
                                disabled={
                                  globalReadWriteAccess ||
                                  globalReadOnlyAccess ||
                                  sfcAccess[key]?.write
                                }
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Checkbox
                                checked={sfcAccess[key]?.write || false}
                                onChange={handleAccessChange(key, "write")}
                                disabled={
                                  globalReadWriteAccess || globalReadOnlyAccess
                                }
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>

          <DialogActions sx={{ mt: 2 }}>
            <Button onClick={handleCloseModal} color="primary">
              Cancel
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={
                formData.email === null ||
                !formData.email?.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/) ||
                (mode !== UPDATE && isEmailDuplicate(formData.email)) ||
                formData.firstName === null ||
                formData.lastName === null ||
                formData.requestType === null ||
                formData.active === undefined ||
                formData.applicationAdministrator === undefined ||
                formData.admin === undefined ||
                formData.globalTechSupport === undefined ||
                formData.userAccessProvisioner === undefined
              }
            >
              {mode === UPDATE ? UPDATE : CREATE}
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UserModelDialog;
